import React, { useState } from 'react'
import { Grid, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { Country } from 'constants/country'
import type { MeetingMode } from 'models/purchaseFlowRevamp'
import { EMAIL_RE } from 'utils/validation/email'
import { InputWrap } from 'components/common/InputWrap'
import { PhoneInput } from 'components/common/PhoneInput'

import { MEETING_MODE_KIND } from '../../meeting.helpers'

export interface MeetingContact {
  name?: string
  phone?: string
  email?: string
}

/**
 * Touched and error state of each mode's contact fields. Held by the section rather than the fields,
 * because a card unmounts its form when it collapses and switching back must not forget it.
 */
export const useMeetingFieldUi = () => {
  const [phoneErrors, setPhoneErrors] = useState<Partial<Record<MeetingMode, string>>>({})
  const [emailTouched, setEmailTouched] = useState<Partial<Record<MeetingMode, boolean>>>({})

  return (mode: MeetingMode) => ({
    phoneError: phoneErrors[mode],
    emailTouched: !!emailTouched[mode],
    setPhoneError: (error: string | undefined) => setPhoneErrors((prev) => ({ ...prev, [mode]: error })),
    touchEmail: () => setEmailTouched((prev) => ({ ...prev, [mode]: true })),
  })
}

export type MeetingFieldUi = ReturnType<ReturnType<typeof useMeetingFieldUi>>

interface Props {
  mode: MeetingMode
  contact: MeetingContact | undefined
  onChange: (patch: MeetingContact) => void
  ui: MeetingFieldUi
  /** Reports the phone input's verdict, which the step's gate cannot judge itself. */
  onPhoneValidityChange: (invalid: boolean) => void
  countryCode?: string
  /** Key under `order:step_instruction` for the note beneath the email. */
  emailHintKey: string
  /** The client's own details rather than somebody else's, which the field labels say. */
  ownDetails?: boolean
}

/** A meeting's contact name, phone and email, as grid items in legacy's two-column order. */
export const MeetingContactFields: React.FC<Props> = ({ mode, contact, onChange, ui, onPhoneValidityChange, countryCode, emailHintKey, ownDetails = false }) => {
  const { t } = useTranslation(['purchase_flow_revamp', 'order', 'instruction_option_field'])
  const label = (field: 'NAME' | 'PHONE' | 'EMAIL') => ownDetails
    ? t(`purchase_flow_revamp:instructions.meeting.self_contact_${field.toLowerCase()}`)
    : t(`instruction_option_field:${MEETING_MODE_KIND[mode]}:${field}`)
  // Optional, but well formed when given: the gate's rule, so the field can say why it blocks.
  const emailError = contact?.email && !EMAIL_RE.test(contact.email)
    ? t('purchase_flow_revamp:instructions.meeting.email_invalid')
    : undefined
  // Shown from the start: a prefilled card often leaves one required field empty, and the disabled CTA alone does not say which.
  const requiredError = t('order:step_instruction.required_field')
  const phoneError = contact?.phone ? ui.phoneError : requiredError

  return (
    <>
      <Grid item xs={12} sm={6}>
        <InputWrap label={label('NAME')} required error={contact?.name ? undefined : requiredError} asLabel>
          <input
            type="text"
            value={contact?.name ?? ''}
            onChange={(e) => onChange({ name: e.target.value || undefined })}
          />
        </InputWrap>
      </Grid>

      <Grid item xs={12} sm={6}>
        <InputWrap label={label('PHONE')} required error={phoneError} asLabel>
          {/* intl-tel-input's .iti wrapper is inline-block and would size to its content. */}
          <Stack sx={{ '& .iti': { display: 'block', width: '100%' }, '& .iti input': { width: '100%' } }}>
            <PhoneInput
              value={contact?.phone ?? ''}
              maxLength={64}
              onChange={(next: string) => onChange({ phone: next || undefined })}
              onValidityChange={(isValid, validationError) => {
                ui.setPhoneError(isValid ? undefined : validationError)
                onPhoneValidityChange(!isValid)
              }}
              initialCountry={countryCode || Country.CH}
            />
          </Stack>
        </InputWrap>
      </Grid>

      <Grid item xs={12} sm={6}>
        <InputWrap
          asLabel
          label={label('EMAIL')}
          error={emailError}
          showError={ui.emailTouched}
          fieldNote={t(`order:step_instruction.${emailHintKey}`)}
        >
          <input
            type="email"
            value={contact?.email ?? ''}
            onChange={(e) => onChange({ email: e.target.value || undefined })}
            onBlur={ui.touchEmail}
          />
        </InputWrap>
      </Grid>
    </>
  )
}
